/* eslint-disable no-nested-ternary */
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { withRouter, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  BackButton,
  Breadcrumbs,
  Text,
  MandatoryFieldText,
  InputContainer,
  SendToast,
  Wrapper,
} from "@networkraildigitalfactory/components";
import moment from "moment";
import { DatePicker } from "antd";
import emojiStrip from "emoji-strip";

import useUser from "../../hooks/useUser";
import ValuesSelect, { SAFETY_PROMISE_VALUE } from "../ValuesSelect/ValuesSelect";
import { AreYouSureModal, CascadingObjectives, ExampleModal } from "../Modals";
import CreateActionButtons from "./CreateActionButtons";
import EditActionButtons from "./EditActionButtons";
import { useUsers, useObjectives } from "../../hooks/useAPI";
import EmployeesSelect from "../EmployeesSelect/EmployeesSelect";
import { a11yGrowTextarea, preventFormDefault } from "../../services/utils";
import StatusLabel from "../StatusLabel/StatusLabel";

import "./AntD.css";
import styles from "./CreateObjective.module.css";

const CreateObjective = (props) => {
  const { editMode, user, isCascaded, setLoading, isDirtyForm, setIsDirtyForm, setNavModalProps } =
    props;
  const history = useHistory();
  const { getValues, control, clearError, register, errors, triggerValidation } = useForm({
    reValidateMode: "onChange",
  });
  const params = useParams();
  const [isActionsDisabled, setIsActionsDisabled] = useState(false);
  const [isDeleteModalOpen, setIsCancelModalOpen] = useState(false);
  const [exampleModalFor, setExampleModalFor] = useState("objectiveDetail");
  const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);
  const [isCascadingObjModalOpen, setIsCascadingObjModalOpen] = useState(false);
  const [objectiveValues, setObjectiveValues] = useState();
  const [objectiveType, setObjectiveType] = useState();

  const detailRef = useRef(null);
  const actionRef = useRef(null);
  const successRef = useRef(null);

  const { isGBR } = useUser();
  const GBR_URL =
    "https://networkrail.sharepoint.com/:p:/r/sites/GBRTT-documents/_layouts/15/Doc.aspx?sourcedoc=%7B5A92FA63-9543-494D-BE85-97BCDE3A343C%7D";

  const NR_URL =
    "https://networkrail.sharepoint.com/sites/myconnect/hr/Pages/Objective-setting.aspx";

  const isSafetyPromise = useMemo(() => {
    return [objectiveType, objectiveValues?.value].some((type) => type === SAFETY_PROMISE_VALUE);
  }, [objectiveType, objectiveValues]);

  const [, getData] = useObjectives({ url: params.id });

  const fetchData = async () => {
    try {
      const result = await getData();
      if (result) {
        setObjectiveValues(result.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const goBack = () => {
    props.history.push(isCascaded ? "/my-team" : "/my-objectives/objectives");
  };
  const toggleDeleteModal = () => {
    setIsCancelModalOpen(!isDeleteModalOpen);
  };

  const toggleExampleModal = () => {
    setIsExampleModalOpen(!isExampleModalOpen);
  };

  const openCascadeModal = async () => {
    await triggerValidation();
    if (Object.keys(errors).length === 0) {
      setIsCascadingObjModalOpen(true);
    }
  };

  const reValidate = () => {
    if (Object.keys(errors).length > 0) {
      triggerValidation();
    }
  };

  const switchExampleModal = (inputFor) => {
    if (isExampleModalOpen) {
      setIsExampleModalOpen(false);
    }
    setExampleModalFor(inputFor);
    setIsExampleModalOpen(true);
  };

  const inputKeydown = () => {
    setIsDirtyForm(true);
  };

  const switchToastMessage = (type) => {
    switch (type) {
      case "draft":
        return "Your objective has been saved as a draft";
      case "edited":
        return "Updates have been saved to draft objective";
      case "pending approval":
        return "Your objective has been sent for approval";
      case "error":
        return "There was a problem updating objective. Please try again.";
      default:
        return "";
    }
  };

  const [, getUser] = useUsers(user.email);

  const [, postData] = useObjectives(
    {
      url: isCascaded ? "cascaded" : "",
      method: "POST",
    },
    { manual: true }
  );

  const saveToCloud = async (data) => {
    try {
      await postData({ data });
      const userData = await getUser();
      props.updateUser(userData.data);
    } catch (err) {
      console.error(err);
      SendToast(switchToastMessage("error"), "error");
    }
  };

  const onSubmit = async (data) => {
    const isValid = await triggerValidation();
    if (isValid) {
      setLoading(true);
      setIsActionsDisabled(true);
      data.status = isCascaded ? "approved" : "pending approval";
      await saveToCloud(data);
      setLoading(false);
      SendToast(
        isCascaded
          ? "The objective has been cascaded to your selected employee(s)"
          : switchToastMessage(data.status),
        "success"
      );
      goBack();
    }
  };

  const onSave = async () => {
    setIsActionsDisabled(true);
    setLoading(true);
    const formData = getValues();
    formData.status = "draft";
    await saveToCloud(formData);
    setLoading(false);
    SendToast("Your objective has been saved as a draft", "success");
    goBack();
  };

  function disabledDate(current) {
    const today = new Date();
    return current && current < moment(today, "YYYY-MM-DD");
  }

  const onInputChange = (field, value) => {
    setObjectiveValues((prevFormValues) => ({
      ...prevFormValues,
      [field]: emojiStrip(value),
    }));
    if (Object.keys(errors).length > 0) {
      triggerValidation();
    }
    setIsDirtyForm(true);
  };

  const [{ error: errPut }, updateObjective] = useObjectives(
    {
      url: params.id,
      method: "PUT",
    },
    { manual: true }
  );

  if (errPut) return SendToast("Your objective hasn't been updated. Please try again", "error");

  const onEdit = async () => {
    const isValid = await triggerValidation();
    if (isValid) {
      try {
        setIsActionsDisabled(true);
        setLoading(true);
        await updateObjective({
          data: {
            ...objectiveValues,
            status: "pending approval",
          },
        });
        setLoading(false);
        setIsDirtyForm(false);
        SendToast(switchToastMessage("pending approval"), "success");
        history.push(`/my-objectives/objectives`);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const onSaveDraft = async (e) => {
    setIsActionsDisabled(true);
    setLoading(true);
    try {
      e.preventDefault();
      const formData = getValues();
      await updateObjective({ data: formData });
      setLoading(false);
      setIsDirtyForm(false);
      SendToast("Your objective has been saved as a draft", "success");
      history.push(`/my-objectives/objectives`);
    } catch (err) {
      console.error(err);
    }
  };

  const getFormattedDate = () => {
    let date;
    if (objectiveValues && objectiveValues.dueDate === null) {
      date = undefined;
    } else {
      date = objectiveValues && moment(new Date(objectiveValues.dueDate), "DD/MM/YYYY");
    }
    return date;
  };

  const getDropdownValue = () => {
    const selectedValue = objectiveValues.value;
    return selectedValue;
  };

  const breadcrumbs = [
    {
      text: isCascaded ? "My Team" : "My Objectives",
      onClick: () => {
        if (isDirtyForm) {
          setNavModalProps({
            show: true,
            navPath: isCascaded ? "/my-team" : "/my-objectives/objectives",
          });
        } else {
          goBack();
        }
      },
    },
    {
      text: `${editMode ? "Edit" : isCascaded ? "Cascade" : "New"}  Objective`,
    },
  ];

  const guidanceAfterSubmitting =
    "When you send for approval your objectives will be sent to your line manager. This gives them the opportunity to confirm and/or discuss further with you.";

  const objectiveNameExample = isGBR ? (
    "For example safety hour for my team."
  ) : (
    <>
      For example safety hour for my team. <br />
      If regarding Safety Promise - enter ‘Safety Promise’ in field below.
    </>
  );

  return (
    <>
      <div className={styles.breadcrumbs_wrapper}>
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <Wrapper maxWidth={1350} align="center">
        {isDirtyForm && (
          <Alert
            color="red"
            title="Unsaved Changes"
            message="You must save or submit your changes before navigating to another page. If you do not select save, all changes will be lost."
            style={{ lineHeight: "140%", letterSpacing: "0.0015em", padding: "8px 10px 8px 10px" }}
          />
        )}
        <div className={styles.row}>
          <BackButton
            text={`back to my ${isCascaded ? "team" : "objectives"}`}
            onClick={() => {
              if (isDirtyForm) {
                setNavModalProps({
                  show: true,
                  navPath: isCascaded ? "/my-team" : "/my-objectives/objectives",
                });
              } else {
                goBack();
              }
            }}
            style={{ lineHeight: "140%", letterSpacing: "0.0015em" }}
          />
          {editMode && (
            <StatusLabel
              status={objectiveValues && objectiveValues.status !== "draft" ? "Editing" : "Draft"}
            />
          )}
        </div>
        <div className={styles.light_bg}>
          <Text
            type="header"
            wrapper="h2"
            style={{ lineHeight: "140%", letterSpacing: "0.0015em" }}
          >
            {editMode
              ? objectiveValues
                ? objectiveValues.name
                : ""
              : isCascaded
                ? "New cascaded objective"
                : "New objective"}
          </Text>
          <MandatoryFieldText />
          <form className={styles.form} role="presentation">
            <div className={styles.input_row}>
              <InputContainer
                labelText="Please give your objective a name"
                exampleText={objectiveNameExample}
                forLabel="name"
                required
                errorText={errors.name && errors.name.message}
              >
                <input
                  name="name"
                  className={`${styles.objective_name} ${styles.form_input} ${
                    errors.name ? styles.error : ""
                  }`}
                  type="text"
                  onKeyDown={preventFormDefault}
                  ref={register({
                    required: "Must not be blank",
                  })}
                  defaultValue={editMode && objectiveValues ? objectiveValues.name : ""}
                  onChange={(e) => {
                    inputKeydown();
                    onInputChange("name", e.target.value, false);
                  }}
                />
              </InputContainer>

              <InputContainer
                labelText="Objective due date"
                exampleText="DD/MM/YYYY"
                forLabel="dueDate"
                required
                errorText={errors.dueDate && errors.dueDate.message}
              >
                {objectiveValues && (
                  <Controller
                    as={
                      <DatePicker
                        format="DD/MM/YYYY"
                        inputReadOnly
                        className={`${styles.form_input} ${errors.dueDate ? styles.error : ""}`}
                        disabledDate={disabledDate}
                        showNow={false}
                        allowClear={false}
                        defaultValue={editMode && getFormattedDate()}
                        onFocus={() => {
                          reValidate();
                        }}
                        getCalendarContainer={(trigger) => trigger.parentElement || document.body}
                      />
                    }
                    valueName="dueDate"
                    defaultValue={editMode && getFormattedDate()}
                    name="dueDate"
                    onChange={([selected]) => {
                      inputKeydown();
                      clearError("dueDate");
                      onInputChange("dueDate", selected.format(), false);
                      return selected;
                    }}
                    control={control}
                    rules={{
                      required: "Must not be blank",
                      validate: () => {
                        const dueDate = getValues("dueDate");
                        const today = new Date();
                        if (dueDate._d < today) {
                          setLoading(false);
                          setIsActionsDisabled(false);
                          return "Due date cannot be in the past";
                        }
                        return true;
                      },
                    }}
                  />
                )}
                {!objectiveValues && (
                  <Controller
                    as={
                      <DatePicker
                        format="DD/MM/YYYY"
                        inputReadOnly
                        className={`${styles.form_input} ${errors.dueDate ? styles.error : ""}`}
                        disabledDate={disabledDate}
                        showNow={false}
                        allowClear={false}
                        defaultValue={null}
                        onFocus={() => {
                          reValidate();
                        }}
                        getCalendarContainer={(trigger) => trigger.parentElement || document.body}
                      />
                    }
                    valueName="dueDate"
                    defaultValue={null}
                    name="dueDate"
                    onChange={([selected]) => {
                      inputKeydown();
                      onInputChange("dueDate", selected.format(), false);
                      return selected;
                    }}
                    control={control}
                    rules={{
                      required: "Must not be blank",
                    }}
                  />
                )}
              </InputContainer>
            </div>
            <InputContainer
              labelText={
                isGBR
                  ? "Please select the GBRTT strategic goal that this objective aligns to"
                  : "Please select the Network Rail vision statement that this objective aligns to"
              }
              exampleText={
                isGBR
                  ? ""
                  : "If regarding Safety Promise - select ‘Safety Promise’ from the drop-down below."
              }
              forLabel="value"
              required
              errorText={errors.value && errors.value.message}
            >
              {objectiveValues && (
                <Controller
                  as={
                    <ValuesSelect
                      className={`${styles.form_input} ${errors.value ? styles.error : ""}`}
                      isGBR={isGBR}
                    />
                  }
                  name="value"
                  role="listbox"
                  valueName="value"
                  onChange={([selected]) => {
                    setObjectiveType(selected);
                    inputKeydown();
                    onInputChange("value", selected, false);
                    return selected;
                  }}
                  onSelect={reValidate}
                  control={control}
                  rules={{
                    required: "Must not be blank",
                  }}
                  defaultValue={editMode && getDropdownValue()}
                />
              )}
              {!objectiveValues && (
                <Controller
                  as={
                    <ValuesSelect
                      className={`${styles.form_input} ${errors.value ? styles.error : ""}`}
                      isGBR={isGBR}
                    />
                  }
                  name="value"
                  valueName="value"
                  onChange={([selected]) => {
                    inputKeydown();
                    return selected;
                  }}
                  onSelect={reValidate}
                  control={control}
                  rules={{
                    required: "Must not be blank",
                  }}
                />
              )}
            </InputContainer>
            {isCascaded && (
              <InputContainer
                labelText="Please select which team members this objective aligns to"
                required
                forLabel="employees"
                errorText={errors.employees && errors.employees.message}
              >
                <Controller
                  as={
                    <EmployeesSelect
                      className={`${styles.form_input} ${errors.employees ? styles.error : ""}`}
                      defaultValue={undefined}
                    />
                  }
                  id="employees"
                  name="employees"
                  valueName="employees"
                  onSelect={reValidate}
                  rules={{
                    required: "Must not be blank",
                    validate: () => {
                      const employees = getValues("employees");
                      if (employees.length === 0) {
                        return "Must not be blank";
                      }
                      return true;
                    },
                  }}
                  control={control}
                />
              </InputContainer>
            )}
            {isGBR && (
              <Text
                type="body"
                wrapper="span"
                size="16px"
                style={{
                  margin: "0.4rem 0 4rem",
                  lineHeight: "140%",
                  letterSpacing: " 0.0015em",
                }}
              >
                Click{" "}
                <a
                  className={styles.myConnectLink}
                  href={GBR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", color: "#37519D" }}
                >
                  here
                </a>{" "}
                to see the Directorate Strategic Goals to help with your objective setting.
              </Text>
            )}
            <Text
              wrapper="h3"
              type="subtitle"
              style={{ fontSize: "20px", lineHeight: "140%", letterSpacing: "0.0015em" }}
            >
              {isGBR
                ? "Setting objectives for both task and behaviours"
                : "Setting SMART objectives"}
            </Text>
            {isGBR && (
              <>
                <Text type="body" wrapper="span" size="16px">
                  To create a clear objective think about{" "}
                  <Text type="body" wrapper="span" size="16px" style={{ fontWeight: 600 }}>
                    WHY
                  </Text>{" "}
                  are we doing this?{" "}
                  <Text type="body" wrapper="span" size="16px" style={{ fontWeight: 600 }}>
                    WHAT
                  </Text>{" "}
                  do we need as the outcome?{" "}
                  <Text type="body" wrapper="span" size="16px" style={{ fontWeight: 600 }}>
                    HOW
                  </Text>{" "}
                  will you achieve this?{" "}
                  <Text type="body" wrapper="span" size="16px" style={{ fontWeight: 600 }}>
                    WHO
                  </Text>{" "}
                  do you need to work with or could help?{" "}
                </Text>
              </>
            )}
            {!isGBR && (
              <>
                <Text type="body" wrapper="span" size="16px">
                  Objectives should be:
                </Text>
                <Text type="body" wrapper="span" size="16px" style={{ margin: "0.4rem 0" }}>
                  Specific, Measurable, Aligned, Realistic and Time-specific.
                </Text>
                <Text
                  type="body"
                  wrapper="span"
                  size="16px"
                  style={{
                    margin: "0.4rem 0",
                    lineHeight: "140%",
                    letterSpacing: " 0.0015em",
                    textDecoration: "underline",
                  }}
                >
                  <a
                    className={styles.myConnectLink}
                    href={NR_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click here to view objectives set by the exec
                  </a>
                </Text>
              </>
            )}

            <div className={styles.textarea_wrapper}>
              <InputContainer
                labelText={
                  isSafetyPromise ? "What’s your Safety Promise?" : "What’s the objective / goal?"
                }
                withExampleLink
                exampleLinkId="objectiveDetailBtn"
                required
                forLabel="objectiveDetail"
                errorText={errors.objectiveDetail && errors.objectiveDetail.message}
                onExampleClick={(e) => {
                  e.preventDefault();
                  switchExampleModal(isSafetyPromise ? "safetyPromise" : "objectiveDetail");
                }}
              >
                <textarea
                  name="objectiveDetail"
                  id="objectiveDetail"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onInputChange("objectiveDetail", e.target.value, true);
                    }
                    a11yGrowTextarea(e, "objectiveDetail");
                  }}
                  ref={
                    isExampleModalOpen
                      ? detailRef
                      : register({
                          required: "Must not be blank",
                        })
                  }
                  className={`${styles.form_textarea} ${
                    errors.objectiveDetail ? styles.error : ""
                  }`}
                  defaultValue={editMode && objectiveValues ? objectiveValues.objectiveDetail : ""}
                  onChange={(e) => {
                    inputKeydown();
                    onInputChange("objectiveDetail", e.target.value, false);
                  }}
                />
              </InputContainer>

              <InputContainer
                labelText="What’s your action plan? How will you achieve this?"
                withExampleLink
                exampleLinkId="actionPlanBtn"
                required
                forLabel="actionPlan"
                errorText={errors.actionPlan && errors.actionPlan.message}
                onExampleClick={(e) => {
                  e.preventDefault();
                  switchExampleModal(isSafetyPromise ? "safetyPromiseActionPlan" : "actionPlan");
                }}
              >
                <textarea
                  name="actionPlan"
                  id="actionPlan"
                  ref={
                    isExampleModalOpen
                      ? actionRef
                      : register({
                          required: "Must not be blank",
                        })
                  }
                  className={`${styles.form_textarea} ${errors.actionPlan ? styles.error : ""}`}
                  defaultValue={editMode && objectiveValues ? objectiveValues.actionPlan : ""}
                  onChange={(e) => {
                    inputKeydown();
                    onInputChange("actionPlan", e.target.value, false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onInputChange("actionPlan", e.target.value, true);
                    }
                    a11yGrowTextarea(e, "actionPlan");
                  }}
                />
              </InputContainer>
              {!isSafetyPromise && (
                <InputContainer
                  labelText="What will success look like?"
                  withExampleLink
                  exampleLinkId="successBtn"
                  required
                  forLabel="whatIsSuccess"
                  errorText={errors.whatIsSuccess && errors.whatIsSuccess.message}
                  onExampleClick={(e) => {
                    e.preventDefault();
                    switchExampleModal("whatIsSuccess");
                  }}
                >
                  <textarea
                    name="whatIsSuccess"
                    id="whatIsSuccess"
                    ref={
                      isExampleModalOpen
                        ? successRef
                        : register({
                            required: "Must not be blank",
                          })
                    }
                    className={`${styles.form_textarea} ${
                      errors.whatIsSuccess ? styles.error : ""
                    }`}
                    defaultValue={editMode && objectiveValues ? objectiveValues.whatIsSuccess : ""}
                    onChange={(e) => {
                      inputKeydown();
                      onInputChange("whatIsSuccess", e.target.value, false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onInputChange("whatIsSuccess", e.target.value, true);
                      }
                      a11yGrowTextarea(e, "whatIsSuccess");
                    }}
                  />
                </InputContainer>
              )}
              <InputContainer
                labelText="Do you have any additional comments?"
                forLabel="additionalComments"
                errorText={errors.additionalComments && errors.additionalComments.message}
              >
                <textarea
                  name="additionalComments"
                  id="additionalComments"
                  ref={register()}
                  className={`${styles.form_textarea} ${
                    errors.additionalComments ? styles.error : ""
                  }`}
                  defaultValue={
                    editMode && objectiveValues ? objectiveValues.additionalComments : ""
                  }
                  onChange={(e) => {
                    inputKeydown();
                    onInputChange("additionalComments", e.target.value, false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onInputChange("additionalComments", e.target.value, true);
                    }
                    a11yGrowTextarea(e, "additionalComments");
                  }}
                />
              </InputContainer>
            </div>
          </form>
        </div>
        <div className={styles.bottom_row}>
          <Text type="body" wrapper="span">
            {isCascaded
              ? "This objective will be sent to all selected team members. They will have the opportunity to edit and re-send for your approval."
              : guidanceAfterSubmitting}
          </Text>
          <div className={styles.button_actions}>
            <div>
              {!editMode && (
                <CreateActionButtons
                  isActionsDisabled={isActionsDisabled}
                  isDirtyForm={isDirtyForm}
                  status={isCascaded ? "new-cascaded" : "new"}
                  saveObjective={onSave}
                  openCascadeModal={openCascadeModal}
                  onSubmit={() => {
                    onSubmit(getValues());
                  }}
                />
              )}
              {editMode && isDirtyForm && (
                <EditActionButtons
                  isActionsDisabled={isActionsDisabled}
                  isDirtyForm={isDirtyForm}
                  status={
                    objectiveValues && objectiveValues.status !== "draft" ? "editing" : "draft"
                  }
                  onSaveDraft={onSaveDraft}
                  toggleDeleteModal={toggleDeleteModal}
                  onEdit={() => {
                    onEdit(getValues());
                  }}
                />
              )}
              {editMode &&
                !isDirtyForm &&
                objectiveValues &&
                objectiveValues.status !== "draft" && (
                  <EditActionButtons
                    isActionsDisabled={isActionsDisabled}
                    isDirtyForm={isDirtyForm}
                    status="editing-no-change"
                    onEdit={() => {
                      onEdit(getValues());
                    }}
                  />
                )}
              {editMode &&
                !isDirtyForm &&
                objectiveValues &&
                objectiveValues.status === "draft" && (
                  <EditActionButtons
                    isActionsDisabled={isActionsDisabled}
                    isDirtyForm={isDirtyForm}
                    status="draft"
                    onSaveDraft={onSaveDraft}
                    toggleDeleteModal={toggleDeleteModal}
                    onEdit={() => {
                      onEdit(getValues());
                    }}
                  />
                )}
            </div>
          </div>
        </div>
        {editMode && (
          <AreYouSureModal
            show={isDeleteModalOpen}
            close={toggleDeleteModal}
            confirm={toggleDeleteModal}
            action="Delete"
            selectedId={props.match.params.id}
          />
        )}
        <ExampleModal
          for={exampleModalFor}
          show={isExampleModalOpen}
          close={toggleExampleModal}
          shards={
            exampleModalFor === "objectiveDetail" // eslint-disable-line no-nested-ternary
              ? [detailRef]
              : exampleModalFor === "actionPlan"
                ? [actionRef]
                : [successRef]
          }
        />
        {isCascaded && (
          <CascadingObjectives
            show={isCascadingObjModalOpen}
            close={() => {
              setIsCascadingObjModalOpen(false);
            }}
            confirm={() => {
              onSubmit(getValues());
            }}
            getSelectedEmployees={() => {
              const { employees } = getValues();
              return employees;
            }}
          />
        )}
      </Wrapper>
    </>
  );
};
export default withRouter(CreateObjective);
